import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { AxiosResponse } from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Chart } from "primereact/chart";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { Utility } from "../../core/Utility";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { getWarehouseDetailsByProductReportTemplate } from "../../document-templates/WarehouseDetailsByProductReportTemplate";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { WarehouseDetailsReport } from "../../models/Report";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getAllDocuments } from "../../services/NewVersionService";
import { getAllProducts } from "../../services/ProductService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import "./WarehouseDetailsByProductReportPage.scss";

export const WarehouseDetailsByProductReportPage = () => {
  const { state } = useApplicationContext();

  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = new Date(businessYear, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31);
  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    if (businessYear === today.getFullYear()) {
      const from = new Date(today.getFullYear(), 0, 1);
      const to = today;
      return { from, to };
    } else {
      return { from: yearStart, to: yearEnd };
    }
  };
  const [dateFrom, setDateFrom] = useState<Date>(
    getDefaultRangeForBusinessYear().from
  );
  const [dateTo, setDateTo] = useState<Date>(
    getDefaultRangeForBusinessYear().to
  );

  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [productCategoriesApi, setProductCategoriesApi] = useState<
    ProductCategory[]
  >([]);
  const [selectedProduct, setSelectedProduct] = useState<Product>(
    {} as Product
  );
  const [selectedProductCategory, setSelectedProductCategory] =
    useState<ProductCategory>({} as ProductCategory);
  const [warehouseDetailsData, setWarehouseDetailsData] = useState<
    WarehouseDetailsReport[]
  >([]);
  const [totalInputNeto, setTotalInputNeto] = useState<number>(0);
  const [totalInputSrps, setTotalInputSrps] = useState<number>(0);
  const [totalOutputNeto, setTotalOutputNeto] = useState<number>(0);
  const [totalOutputSrps, setTotalOutputSrps] = useState<number>(0);
  const [processing, setProcessing] = useState(false);

  const { t } = useTranslation();
  const toast = useRef(null);

  const [pdfData, setPdfData] = useState<any>(
    getWarehouseDetailsByProductReportTemplate(
      warehouseDetailsData,
      selectedProduct.name ?? selectedProductCategory.name,
      totalInputNeto,
      totalInputSrps,
      totalOutputNeto,
      totalOutputSrps
    )
  );
  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  const [graphData, setGraphData] = useState<any>({
    labels: [],
    datasets: [
      {
        label: t("Ulaz neto"),
        backgroundColor: "#198754",
        data: [],
      },
      {
        label: t("Izlaz neto"),
        backgroundColor: "#495057",
        data: [],
      },
      {
        label: t("Ulaz - izlaz"),
        backgroundColor: "#A9E1C5",
        data: [],
      },
    ],
  });

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookups = async () => {
    const [pResp, cResp]: [AxiosResponse, AxiosResponse] = await Promise.all([
      getAllProducts(),
      getAllProductCategories(),
    ]);
    [pResp, cResp].forEach(ensureSuccess);
    setProductsApi(pResp?.data?.result ?? []);
    setProductCategoriesApi(cResp?.data?.result ?? []);
  };

  useEffect(() => {
    setProcessing(true);
    refreshLookups()
      .catch((e) => {
        console.log(e);
        //@ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Greška pri učitavanju šifarnika"),
          life: 3000,
        });
      })
      .finally(() => setProcessing(false));
  }, []);

  useEffect(() => {
    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
  }, [businessYear]);

  useEffect(() => {
    if (productsApi.length === 0 || productCategoriesApi.length === 0) return;

    const run = async () => {
      setProcessing(true);
      try {
        await formatProductInWarehousesReportData();
      } finally {
        setProcessing(false);
      }
    };

    run();
  }, [
    productsApi.length,
    productCategoriesApi.length,
    selectedProduct?.id,
    selectedProductCategory?.id,
    dateFrom,
    dateTo,
  ]);

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getWarehouseDetailsByProductReportTemplate(
      warehouseDetailsData,
      selectedProduct.name ?? selectedProductCategory.name ?? "Sve",
      totalInputNeto,
      totalInputSrps,
      totalOutputNeto,
      totalOutputSrps
    );
    setPdfData(newPdf);
  }, [
    warehouseDetailsData,
    selectedProduct?.name,
    selectedProductCategory?.name,
    totalInputNeto,
    totalInputSrps,
    totalOutputNeto,
    totalOutputSrps,
  ]);

  const formatProductInWarehousesReportData = async () => {
    // uvek računamo od nule za izabrani period/proizvod
    let copyOfWarehouseDetailsData: WarehouseDetailsReport[] = [];

    // povuci ulaze i izlaze merkantile
    const [inputsResponse, outputsResponse]: [AxiosResponse, AxiosResponse] =
      await Promise.all([
        getAllDocuments(true, true, dateFrom, dateTo), // prijemnice merkantile
        getAllDocuments(true, false, dateFrom, dateTo), // otpremnice merkantile
      ]);

    const inputDocs = inputsResponse.data.result;
    const outputDocs = outputsResponse.data.result;

    // helper za filter po kategoriji/proizvodu (koristi categoryId/productId iz BE)
    const matchesCategoryOrProduct = (doc: any) => {
      if (selectedProductCategory?.id === undefined) return true;

      if (selectedProduct?.id === undefined) {
        // filtriramo po grupi robe
        return (
          selectedProductCategory.id?.toString() === doc.categoryId?.toString()
        );
      }

      // filtriramo po konkretnoj robi
      return selectedProduct.id?.toString() === doc.productId?.toString();
    };

    // ⬇️ ULAZI
    for (const mercantileInput of inputDocs) {
      if (
        mercantileInput?.isDocumentFinished !== true || // samo zaključeni
        !matchesCategoryOrProduct(mercantileInput)
      ) {
        continue;
      }

      const warehouseName = mercantileInput.warehouseName as string;
      const netto = mercantileInput.netto ?? 0;
      const srps = mercantileInput.srps ?? 0;

      const idx = copyOfWarehouseDetailsData.findIndex(
        (item) => item.warehouseName === warehouseName
      );

      if (idx >= 0) {
        const row = copyOfWarehouseDetailsData[idx];

        // ponderisan prosek ulaznih parametara
        row.inputMoisture = parseFloat(
          (
            (row.inputNetto * (row.inputMoisture ?? 0) +
              netto * (mercantileInput.moisture ?? 0)) /
            (row.inputNetto + netto || 1)
          ).toFixed(2)
        );
        row.inputImpurity = parseFloat(
          (
            (row.inputNetto * (row.inputImpurity ?? 0) +
              netto * (mercantileInput.impurity ?? 0)) /
            (row.inputNetto + netto || 1)
          ).toFixed(2)
        );
        if (mercantileInput.breakage !== null) {
          row.inputBreakage = parseFloat(
            (
              (row.inputNetto * (row.inputBreakage ?? 0) +
                netto * (mercantileInput.breakage ?? 0)) /
              (row.inputNetto + netto || 1)
            ).toFixed(2)
          );
        }
        if (mercantileInput.defect !== null) {
          row.inputDefect = parseFloat(
            (
              (row.inputNetto * (row.inputDefect ?? 0) +
                netto * (mercantileInput.defect ?? 0)) /
              (row.inputNetto + netto || 1)
            ).toFixed(2)
          );
        }
        if (mercantileInput.protein !== null) {
          row.inputProtein = parseFloat(
            (
              (row.inputNetto * (row.inputProtein ?? 0) +
                netto * (mercantileInput.protein ?? 0)) /
              (row.inputNetto + netto || 1)
            ).toFixed(2)
          );
        }
        if (mercantileInput.hectoliter) {
          row.inputHectoliter = parseFloat(
            (
              (row.inputNetto * (row.inputHectoliter ?? 0) +
                netto * (mercantileInput.hectoliter ?? 0)) /
              (row.inputNetto + netto || 1)
            ).toFixed(2)
          );
        }

        row.inputNetto += netto;
        row.inputSrps += srps;
      } else {
        copyOfWarehouseDetailsData.push({
          id: mercantileInput.id,
          warehouseName,
          inputNetto: netto,
          inputMoisture: mercantileInput.moisture ?? 0,
          inputImpurity: mercantileInput.impurity ?? 0,
          inputBreakage: mercantileInput.breakage ?? undefined,
          inputDefect: mercantileInput.defect ?? undefined,
          inputProtein: mercantileInput.protein ?? undefined,
          inputHectoliter: mercantileInput.hectoliter ?? undefined,
          inputSrps: srps,
          inputDiference: 0,
          outputNetto: 0,
          outputMoisture: 0,
          outputImpurity: 0,
          outputBreakage: undefined,
          outputDefect: undefined,
          outputProtein: undefined,
          outputHectoliter: undefined,
          outputSrps: 0,
          outputDiference: 0,
          totalDiference: 0,
        });
      }
    }

    // ⬇️ IZLAZI
    for (const mercantileOutput of outputDocs) {
      if (
        mercantileOutput?.isDocumentFinished !== true ||
        !matchesCategoryOrProduct(mercantileOutput)
      ) {
        continue;
      }

      const warehouseName = mercantileOutput.warehouseName as string;
      const netto = mercantileOutput.netto ?? 0;
      const srps = mercantileOutput.srps ?? 0;

      const idx = copyOfWarehouseDetailsData.findIndex(
        (item) => item.warehouseName === warehouseName
      );

      if (idx >= 0) {
        const row = copyOfWarehouseDetailsData[idx];

        row.outputMoisture = parseFloat(
          (
            (row.outputNetto * (row.outputMoisture ?? 0) +
              netto * (mercantileOutput.moisture ?? 0)) /
            (row.outputNetto + netto || 1)
          ).toFixed(2)
        );
        row.outputImpurity = parseFloat(
          (
            (row.outputNetto * (row.outputImpurity ?? 0) +
              netto * (mercantileOutput.impurity ?? 0)) /
            (row.outputNetto + netto || 1)
          ).toFixed(2)
        );
        if (mercantileOutput.breakage !== null) {
          row.outputBreakage = parseFloat(
            (
              (row.outputNetto * (row.outputBreakage ?? 0) +
                netto * (mercantileOutput.breakage ?? 0)) /
              (row.outputNetto + netto || 1)
            ).toFixed(2)
          );
        }
        if (mercantileOutput.defect !== null) {
          row.outputDefect = parseFloat(
            (
              (row.outputNetto * (row.outputDefect ?? 0) +
                netto * (mercantileOutput.defect ?? 0)) /
              (row.outputNetto + netto || 1)
            ).toFixed(2)
          );
        }
        if (mercantileOutput.protein !== null) {
          row.outputProtein = parseFloat(
            (
              (row.outputNetto * (row.outputProtein ?? 0) +
                netto * (mercantileOutput.protein ?? 0)) /
              (row.outputNetto + netto || 1)
            ).toFixed(2)
          );
        }
        if (mercantileOutput.hectoliter) {
          row.outputHectoliter = parseFloat(
            (
              (row.outputNetto * (row.outputHectoliter ?? 0) +
                netto * (mercantileOutput.hectoliter ?? 0)) /
              (row.outputNetto + netto || 1)
            ).toFixed(2)
          );
        }

        row.outputNetto += netto;
        row.outputSrps += srps;
      } else {
        copyOfWarehouseDetailsData.push({
          id: mercantileOutput.id,
          warehouseName,
          inputNetto: 0,
          inputMoisture: 0,
          inputImpurity: 0,
          inputBreakage: undefined,
          inputDefect: undefined,
          inputProtein: undefined,
          inputHectoliter: undefined,
          inputSrps: 0,
          inputDiference: 0,
          outputNetto: netto,
          outputMoisture: mercantileOutput.moisture ?? 0,
          outputImpurity: mercantileOutput.impurity ?? 0,
          outputBreakage: mercantileOutput.breakage ?? undefined,
          outputDefect: mercantileOutput.defect ?? undefined,
          outputProtein: mercantileOutput.protein ?? undefined,
          outputHectoliter: mercantileOutput.hectoliter ?? undefined,
          outputSrps: srps,
          outputDiference: 0,
          totalDiference: 0,
        });
      }
    }

    // razlike
    for (const row of copyOfWarehouseDetailsData) {
      row.inputDiference = row.inputNetto - row.inputSrps;
      row.outputDiference = row.outputNetto - row.outputSrps;
      row.totalDiference = row.inputSrps - row.outputSrps;
    }

    // ukupni sabiri
    let newTotalInputNetoValue = 0;
    let newTotalInputSrpsValue = 0;
    let newTotalOutputNetoValue = 0;
    let newTotalOutputSrpsValue = 0;

    for (const row of copyOfWarehouseDetailsData) {
      newTotalInputNetoValue += row.inputNetto;
      newTotalInputSrpsValue += row.inputSrps;
      newTotalOutputNetoValue += row.outputNetto;
      newTotalOutputSrpsValue += row.outputSrps;
    }

    setTotalInputNeto(newTotalInputNetoValue);
    setTotalInputSrps(newTotalInputSrpsValue);
    setTotalOutputNeto(newTotalOutputNetoValue);
    setTotalOutputSrps(newTotalOutputSrpsValue);

    // graf
    const newGraphData = cloneDeep(graphData);
    newGraphData.labels = [];
    newGraphData.datasets[0].data = [];
    newGraphData.datasets[1].data = [];
    newGraphData.datasets[2].data = [];

    for (const item of copyOfWarehouseDetailsData) {
      newGraphData.labels.unshift(item.warehouseName);
      newGraphData.datasets[0].data.unshift(item.inputNetto);
      newGraphData.datasets[1].data.unshift(item.outputNetto);
      newGraphData.datasets[2].data.unshift(item.totalDiference);
    }

    setGraphData(newGraphData);
    setWarehouseDetailsData(copyOfWarehouseDetailsData);
  };

  const handleProductChange = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(selectedProduct);
    copyOfNewProduct = productsApi.find((item) => item.id == value) as Product;
    setSelectedProduct(copyOfNewProduct);
  };

  const handleProductCategoryChange = (prop: string, value: any) => {
    setSelectedProduct({} as Product);
    const copyOfNewProductCategory = productCategoriesApi.find(
      (item) => item.id == value
    ) as ProductCategory;
    setSelectedProductCategory(copyOfNewProductCategory);
  };

  const inputNetto = (rowData: WarehouseDetailsReport) => {
    return Utility.addThousandSeparator(rowData.inputNetto);
  };

  const inputSrps = (rowData: WarehouseDetailsReport) => {
    return Utility.addThousandSeparator(rowData.inputSrps);
  };

  const inputDiference = (rowData: WarehouseDetailsReport) => {
    return Utility.addThousandSeparator(rowData.inputDiference);
  };

  const outputNetto = (rowData: WarehouseDetailsReport) => {
    return Utility.addThousandSeparator(rowData.outputNetto);
  };

  const outputSrps = (rowData: WarehouseDetailsReport) => {
    return Utility.addThousandSeparator(rowData.outputSrps);
  };

  const outputDiference = (rowData: WarehouseDetailsReport) => {
    return Utility.addThousandSeparator(rowData.outputDiference);
  };

  const totalDiference = (rowData: WarehouseDetailsReport) => {
    return Utility.addThousandSeparator(rowData.totalDiference);
  };

  const renderHeader = () => {
    return (
      <div className="header-title">
        <span></span>
        <span className="header-title-field">{t("ULAZ ROBE")}</span>
        <span className="header-title-field">{t("IZLAZ ROBE")}</span>
        <span></span>
      </div>
    );
  };

  const {
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-shop-window header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
                transform: "translate(0, 5px)",
              }}
            ></i>
            <span className="header-title">
              {t("Analiza magacina za proizvod")}
            </span>
            <DropdownInput
              id="productCategory"
              value={selectedProductCategory?.id}
              onValueChange={handleProductCategoryChange}
              optionValue="id"
              optionLabel="name"
              options={productCategoriesApi.filter(
                (item) => item.type == ProductType.MercantileGoods
              )}
              filter={false}
              labelText=""
              placeholderText={t("grupa robe")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={processing}
            />
            <span className="header-elements"></span>
            <DropdownInput
              id="product"
              value={selectedProduct?.id}
              onValueChange={handleProductChange}
              optionValue="id"
              optionLabel="name"
              options={productsApi.filter(
                (item) => item.category?.id === selectedProductCategory?.id
              )}
              filter={false}
              labelText=""
              placeholderText={t("Naziv robe")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={selectedProductCategory?.name === undefined}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (selectedProductCategory?.id) {
                  if (downloadPdfDocument.current) {
                    // @ts-ignore
                    downloadPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(`Nije izabrana grupa proizvoda`),
                    life: 3000,
                  });
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (selectedProductCategory?.id) {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(`Nije izabrana grupa proizvoda`),
                    life: 3000,
                  });
                }
              }}
            />
          </div>
        </div>
        <div className="wrapper-data-table">
          {processing ? (
            <ProgressSpinner
              style={{ width: "100px", height: "100px", color: "green" }}
              strokeWidth="4"
              animationDuration=".8s"
            />
          ) : (
            <DataTable
              value={warehouseDetailsData}
              header={renderHeader}
              showGridlines
              stripedRows
            >
              <Column
                field="warehouseName"
                header={t("MAGACIN")}
                footer={t("Ukupno")}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={inputNetto}
                header={`${t("NETO")} (kg)`}
                footer={Utility.addThousandSeparator(totalInputNeto)}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                style={{ fontWeight: "bold" }}
              />
              <Column
                field="inputMoisture"
                header={`${t("Vlaga")} (%)`}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                hidden={selectedProductCategory?.id ? false : true}
              />
              <Column
                field="inputImpurity"
                header={`${t("Prim.")} (%)`}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                hidden={selectedProductCategory?.id ? false : true}
              />
              <Column
                field="inputBreakage"
                header={`${t("Lom")} (%)`}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                hidden={
                  selectedProductCategory?.name == "Kukuruz" ? false : true
                }
              />
              <Column
                field="inputDefect"
                header={`${t("Def.Z")} (%)`}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                hidden={
                  selectedProductCategory?.name == "Kukuruz" ? false : true
                }
              />
              <Column
                field="inputProtein"
                header={`${t("Prot.")} (%)`}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                hidden={
                  selectedProductCategory?.name == "Pšenica" ? false : true
                }
              />
              <Column
                field="inputHectoliter"
                header={`${t("H.Tež.")} (kg/hl)`}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                hidden={
                  selectedProductCategory?.name == "Pšenica" ||
                  selectedProductCategory?.name == "Ječam"
                    ? false
                    : true
                }
              />
              <Column
                body={inputSrps}
                header={`${t("SRPS")} (kg)`}
                footer={Utility.addThousandSeparator(totalInputSrps)}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                style={{ fontWeight: "bold" }}
              />
              <Column
                body={inputDiference}
                header={`${t("NETO-SRPS")} (kg)`}
                footer={Utility.addThousandSeparator(
                  totalInputNeto - totalInputSrps
                )}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={outputNetto}
                header={`${t("NETO")} (kg)`}
                footer={Utility.addThousandSeparator(totalOutputNeto)}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                style={{ color: "#DF5461", fontWeight: "bold" }}
              />
              <Column
                field="outputMoisture"
                header={`${t("Vlaga")} (%)`}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                style={{ color: "#DF5461" }}
                hidden={selectedProductCategory?.id ? false : true}
              />
              <Column
                field="outputImpurity"
                header={`${t("Prim.")} (%)`}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                style={{ color: "#DF5461" }}
                hidden={selectedProductCategory?.id ? false : true}
              />
              <Column
                field="outputBreakage"
                header={`${t("Lom")} (%)`}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                style={{ color: "#DF5461" }}
                hidden={
                  selectedProductCategory?.name == "Kukuruz" ? false : true
                }
              />
              <Column
                field="outputDefect"
                header={`${t("Def.Z")} (%)`}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                style={{ color: "#DF5461" }}
                hidden={
                  selectedProductCategory?.name == "Kukuruz" ? false : true
                }
              />
              <Column
                field="outputProtein"
                header={`${t("Prot.")} (%)`}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                hidden={
                  selectedProductCategory?.name == "Pšenica" ? false : true
                }
              />
              <Column
                field="outputHectoliter"
                header={`${t("H.Tež.")} (kg/hl)`}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                style={{ color: "#DF5461" }}
                hidden={
                  selectedProductCategory?.name == "Pšenica" ||
                  selectedProductCategory?.name == "Ječam"
                    ? false
                    : true
                }
              />
              <Column
                body={outputSrps}
                header={`${t("SRPS")} (kg)`}
                footer={Utility.addThousandSeparator(totalOutputSrps)}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                style={{ color: "#DF5461", fontWeight: "bold" }}
              />
              <Column
                body={outputDiference}
                header={`${t("NETO-SRPS")} (kg)`}
                footer={Utility.addThousandSeparator(
                  totalOutputNeto - totalOutputSrps
                )}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                style={{ color: "#DF5461" }}
              />
              <Column
                body={totalDiference}
                header={`${t("ULAZ - IZLAZ")} (kg)`}
                footer={Utility.addThousandSeparator(
                  totalInputNeto - totalOutputNeto
                )}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
                style={{ color: "#F1CB45", fontWeight: "bold" }}
              />
            </DataTable>
          )}
        </div>
        <div className="document-preview">
          <PdfPreview
            pdfDefinition={pdfData}
            downloadDocument={downloadPdfDocument}
            printDocument={printPdfDocument}
            documentName={`${t("Analiza po magacinima za proizvod ")}${
              selectedProduct.name ?? selectedProductCategory.name
            }_${Utility.formatDate(new Date())}.pdf`}
          />
        </div>
        {warehouseDetailsData.length > 0 && (
          <div className="card">
            <Chart type="bar" data={graphData} />
          </div>
        )}
      </div>
    </PageLayout>
  );
};
